import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import emojiRegexRGI from 'emoji-regex/es2015/RGI_Emoji';
import Button from '../../../atoms/buttons/Button';
import { commonIcons } from '../../../atoms/icons/materialIcons';
import { getBorders, getInputTheme } from '../../../style';
import { handlePostTypingStatus, triggerPostMessageProcess } from '../../../actions/mailActions';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import breakpoints from '../../../theme/material/breakpoints';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { IThread } from 'actions/types/mailActions';

interface ConversationTextAreaProps {
  disableSend: boolean;
  username: string;
  otherAccountHashId: string;
  hidePickers: () => void;
  scrollToBottom: () => void;
  message: string;
  setMessage: any;
  typingIndicator: boolean;
  setTypingIndicator: any;
  thread: IThread;
}

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const TextAreaStyled = styled(TextareaAutosize)(() => ({
  width: '100%',
  border: getBorders().PRIMARY,
  padding: '10px 40px 10px 10px',
  outline: 'none',
  borderRadius: 3,
  fontSize: 16,
  margin: 0,
  lineHeight: 1,
  fontFamily: 'inherit',
  maxHeight: 70,
  resize: 'none',
  background: getInputTheme().backgroundInput,
  color: getInputTheme().colorInput,
  [breakpoints.down(639.05)]: {
    marginBottom: 5,
  },
}));

const mobileSendBtnStyle = {
  height: 45,
  position: 'absolute',
  zIndex: 10,
  width: 50,
  right: -8,
  top: -10,
};

const desktopSendBtnStyle = {
  overflow: 'visible',
  height: 29,
  width: 29,
  padding: 0,
  position: 'absolute',
  top: 9,
  right: 16,
};
const ConversationTextArea: React.FC<ConversationTextAreaProps> = ({
  disableSend,
  username,
  hidePickers,
  scrollToBottom,
  message,
  setMessage,
  typingIndicator,
  setTypingIndicator,
  otherAccountHashId,
  thread,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const conversationTextRef = useRef<HTMLTextAreaElement>(null);
  const { isMobile } = useMediaBreakpoints();
  const { showMandatoryWall } = useMandatoryWall();

  const handleSendMessage = (message: string, type = '', source = null, extension = null) => {
    dispatch(triggerPostMessageProcess(message, type, source, extension, params, navigate, location));
  };

  const handleSendTypingStatus = () => {
    if (params.hashId) {
      dispatch(handlePostTypingStatus(otherAccountHashId, thread.uid));
    }
  };

  const handlePostMessage = () => {
    if (message.length < 1 || disableSend) return;
    setMessage('');
    hidePickers();

    const emojiArr = [];

    if (message.length < 7) {
      const regex = emojiRegexRGI();
      const strMatched = message.match(/.{1,2}/g);

      if (strMatched) {
        strMatched.map(str => {
          let match;

          while ((match = regex.exec(str))) {
            // weird workaround not working without this while statement
          }
          emojiArr.push(regex.exec(str) !== null);
          return match;
        });
      }
    }

    const nonEmoji = emojiArr.includes(false) || message.length > 6;

    showMandatoryWall(
      () => {
        handleSendMessage(message, nonEmoji ? '' : 'emoji');

        scrollToBottom();
      },
      () => undefined,
      'sendMessage',
      false,
      ['profileWall']
    );
  };

  const handleChangeMessage = e => {
    const message = e.target.value;

    setMessage(message);

    if (!typingIndicator && message.trim() !== '') {
      handleSendTypingStatus();
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.MESSAGE_TYPED,
        payload: {},
      });
      setTypingIndicator(true);

      setTimeout(() => {
        setTypingIndicator(false);
      }, 6000);
    }
  };

  const handleKeyDownMessage = e => {
    if (e.keyCode == 13 && e.shiftKey == false) {
      e.preventDefault();
      handlePostMessage();
    }
  };

  useEffect(() => {
    if (conversationTextRef?.current && !disableSend) {
      conversationTextRef.current.focus();
    }
  }, [conversationTextRef, disableSend]);

  return (
    <>
      <Button
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        customStyle={isMobile ? mobileSendBtnStyle : desktopSendBtnStyle}
        onClick={handlePostMessage}
        buttonType="chromeless"
        disabled={disableSend || !message}
        data-test-id="send-message-button"
      >
        {disableSend || !message ? <commonIcons.send.inactive /> : <commonIcons.send.default />}
      </Button>
      <TextAreaStyled
        ref={conversationTextRef}
        placeholder={disableSend ? 'Sending...' : `Message ${username}...`}
        id={isMobile ? 'textAreaStyled' : ''}
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        elastic="true"
        value={message}
        onChange={handleChangeMessage}
        onKeyDown={handleKeyDownMessage}
        onClick={hidePickers}
        disabled={disableSend}
        style={{ cursor: disableSend ? 'not-allowed' : 'unset' }}
      />
    </>
  );
};

export default ConversationTextArea;
